import { ThemeOptions } from '@mui/material/styles/createTheme';

import { tokens } from '../../../theme/generated/tokens';
import { cssVars } from '../../../utils/cssVars';

const { getters, setValues } = cssVars(
  {
    height: '40px',
    horizontalPadding: '10px',
    adornmentSpacing: '8px',
    fontSize: '14px',
    lineHeight: '22px',
  },
  'ds-input'
);

export const inputGetters = getters;

export const inputTheme: ThemeOptions['components'] = {
  DsInput: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          display: 'flex',
          flexDirection: 'column',
          ...theme.typography.P16R,

          '.MuiInputLabel': {
            lineHeight: '18px',
            '&-sizeSmall': {
              lineHeight: '16px',
            },
          },

          '& .MuiFilledInput-root': {
            height: getters.height(),
            paddingLeft: getters.horizontalPadding(),
            paddingRight: getters.horizontalPadding(),
            '&.MuiInputBase-formControl': {
              backgroundColor: tokens.theme.bg.structural.surface,
              border: `1px solid ${tokens.theme.border.structural.neutral}`,
              borderRadius: tokens.theme.radius.sm,
              '&:after, &:before': {
                display: 'none',
              },
              '&.DS-Input-readOnly': {
                backgroundColor: tokens.theme.readonly.bg,
                '& .MuiInputBase-input': {
                  color: tokens.theme.readonly.fg,
                },
                '&.Mui-focused': {
                  boxShadow: 'none',
                },
              },
            },
            '&:hover': {
              borderColor: tokens.theme.border.action.neutralHover,
            },
            '&.Mui-error': {
              borderColor: tokens.theme.border.status.error,
            },
            '&.DS-success': {
              borderColor: tokens.theme.border.status.success,
            },
            '&.Mui-focused': {
              borderColor: tokens.theme.border.selection.primary,
              // TODO (Justas): Verify token usage - shadow from Figma is 0px 0px 1px #82b4ff
              boxShadow: `0px 0px 1px ${tokens.theme.border.selection.primary}`,
            },
            '&.DS-Input-readOnly': {
              borderColor: tokens.theme.border.structural.neutral,
            },
            '&.Mui-disabled': {
              color: tokens.theme.disabled.fg,
              borderColor: tokens.theme.disabled.border,
              '& .DS-Input-adornment-root': {
                color: 'inherit',
              },
            },
          },

          '& .MuiInputBase-input': {
            margin: 0,
            padding: 0,
            color: tokens.theme.fg.structural.primary,
            fontSize: getters.fontSize(),
            lineHeight: getters.lineHeight(),
            '&::placeholder': {
              color: tokens.theme.fg.structural.secondary,
              opacity: 'unset',
            },
            '&:focus::placeholder': {
              color: tokens.theme.fg.structural.secondary,
            },
            '&::selection': {
              backgroundColor: tokens.theme.bg.selection.primary,
              color: tokens.theme.fg.selection.onPrimary,
            },
          },

          '& .DS-Input-adornment-root': {
            fontSize: getters.fontSize(),
            lineHeight: getters.lineHeight(),
            color: tokens.theme.fg.structural.secondary,
            height: '100%',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            '& svg': {
              color: 'inherit',
              fontSize: '1.2em',
            },
            '&.Mui-disabled': {
              color: tokens.theme.disabled.fg,
            },
          },
          '& .DS-Input-startAdornment-root': {
            paddingRight: getters.adornmentSpacing(),
          },
          '& .DS-Input-endAdornment-root': {
            paddingLeft: getters.adornmentSpacing(),
          },
          '& .MuiInputLabel-root': {
            position: 'relative',
            transform: 'unset',
          },

          '& .MuiInputBase-input[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },

          // Chrome renders time-input fields via internal pseudo-elements that
          // don't reliably inherit `color`; set explicitly.
          '& .MuiInputBase-input[type=time]': {
            '&::-webkit-datetime-edit, &::-webkit-datetime-edit-fields-wrapper, &::-webkit-datetime-edit-hour-field, &::-webkit-datetime-edit-minute-field, &::-webkit-datetime-edit-ampm-field, &::-webkit-datetime-edit-text':
              {
                color: tokens.theme.fg.structural.primary,
              },
            '&::-webkit-calendar-picker-indicator': {
              color: tokens.theme.fg.structural.secondary,
              cursor: 'pointer',
            },
          },
        };
      },
      small: setValues({
        height: '28px',
        horizontalPadding: '8px',
        adornmentSpacing: '4px',
        fontSize: '12px',
        lineHeight: '18px',
      }),
      medium: setValues({
        height: '32px',
        horizontalPadding: '10px',
        adornmentSpacing: '8px',
        fontSize: '14px',
        lineHeight: '22px',
      }),
      large: setValues({
        height: '40px',
        horizontalPadding: '12px',
        adornmentSpacing: '8px',
        fontSize: '14px',
        lineHeight: '22px',
      }),
    },
  },
};
